import React, { useState, useRef, useEffect } from 'react';
import { suggestAddress } from '../services/api';
import { useLang } from '../context/LanguageContext';

export default function AddressInput({ value, onChange, onSelect, onBlur, placeholder, required }) {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [suggestError, setSuggestError] = useState(false);
  const { t } = useLang();
  const debounceRef = useRef(null);
  const containerRef = useRef(null);
  const justSelectedRef = useRef(false);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleChange = (e) => {
    const val = e.target.value;
    onChange(val);
    clearTimeout(debounceRef.current);
    setSuggestError(false);
    if (val.length < 3) {
      setSuggestions([]);
      setOpen(false);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      setFetching(true);
      try {
        const res = await suggestAddress(val);
        setSuggestions(res.data);
        setOpen(res.data.length > 0);
      } catch {
        setSuggestions([]);
        setOpen(false);
        setSuggestError(true);
      } finally {
        setFetching(false);
      }
    }, 400);
  };

  const handleSelect = (s) => {
    justSelectedRef.current = true;
    onSelect({ text: s.display_name, lat: parseFloat(s.lat), lng: parseFloat(s.lon) });
    setSuggestions([]);
    setOpen(false);
  };

  const handleBlur = () => {
    if (justSelectedRef.current) {
      justSelectedRef.current = false;
      return;
    }
    setTimeout(() => setOpen(false), 150);
    if (onBlur) onBlur();
  };

  return (
    <div ref={containerRef} className="relative flex-1 min-w-0">
      <div className="flex items-center gap-1">
        <input
          value={value}
          onChange={handleChange}
          onBlur={handleBlur}
          required={required}
          placeholder={placeholder}
          autoComplete="new-password"
          className="flex-1 bg-transparent outline-none text-sm font-medium text-white placeholder-white/30 min-w-0"
        />
        {fetching && (
          <svg className="animate-spin w-3 h-3 text-white/40 shrink-0" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/>
          </svg>
        )}
      </div>
      {suggestError && !open && (
        <p className="absolute z-50 left-0 right-0 top-full mt-1 text-xs text-amber-400">
          {t.rideForm?.suggestError}
        </p>
      )}
      {open && suggestions.length > 0 && (
        <ul className="absolute z-50 left-0 right-0 top-full mt-1 bg-white rounded-xl shadow-lg border border-slate-100 overflow-hidden max-h-52 overflow-y-auto">
          {suggestions.map((s, i) => (
            <li key={i}>
              <button
                type="button"
                onMouseDown={() => handleSelect(s)}
                className="w-full text-left px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 truncate"
              >
                {s.display_name}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
